import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { WIDGET_ATTRIBUTE } from '../skjermbilde/typer.ts';
import { Ikon } from '../Ikon.tsx';
import { tekst } from './tekster.ts';

/**
 * DD-47 — «Widgeten er en dialog og oppfører seg som en.» `role="dialog"`,
 * `aria-modal`, fokus inn ved åpning og FANGET der, ESC og ✕ lukker, fokus
 * tilbake til vertens knapp ved lukking. Vertens innhold får `inert` bak
 * dialogen, så skjermlesere ikke ser det.
 *
 * Form: modal på desktop, bunnark på smal skjerm — målt med `matchMedia` mot
 * `--innspel-layout-breakpoint` fra tokens, aldri user-agent. Samme flyt, annen
 * form (wireframe 2b, mobilraden).
 *
 * Presisert 2026-09-14 (UX-gjennomgangen C2, C11):
 *
 * - **Primærknappen står i `footer`**, utenfor `innspel-dialog-kropp` som ruller.
 *   Slotten fantes fra M1.5 og ble aldri brukt; «Send» lå i stabelen og forsvant
 *   under folden med bilde og kontekst åpent.
 * - **Fokus lander på DIALOGEN**, ikke på ✕ «Lukk». Første fokuserbare element er
 *   avbryt-knappen, og en skjermleser leste «Lukk» som det første i flyten.
 * - **`aria-label` følger steget**, og steget annonseres i en live-region: uten
 *   den er et stegbytte usynlig for en skjermleser, siden dialogen ikke lukkes.
 * - **Scrim-klikk kan nektes** (`kanLukkeVedScrim`): et feilklikk ved siden av
 *   dialogen skal ikke kaste 120 tegn.
 *
 * `data-innspel-widget` på rota gjør at skjermbilde-rendreren utelater
 * dialogen fra bildet (K4).
 */
export function Dialog({
  open,
  onClose,
  children,
  footer,
  label,
  kanLukkeVedScrim = true,
  colorScheme,
  returnFocusTo,
}: {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  /** Etiketten for DETTE steget (DD-47). Endres den, annonseres den. */
  label?: string;
  /** `false` når et klikk utenfor ville kastet noe innsenderen har skrevet (C1). */
  kanLukkeVedScrim?: boolean;
  colorScheme?: 'dark' | 'light';
  /** Elementet fokus skal tilbake til ved lukking — vertens knapp (DD-47). */
  returnFocusTo?: HTMLElement | null;
}) {
  const rot = useRef<HTMLDivElement | null>(null);
  const sisteFokus = useRef<HTMLElement | null>(null);
  const form = useForm();
  const etikett = label ?? tekst.dialogEtikett;

  useLayoutEffect(() => {
    if (!open) return;
    sisteFokus.current = returnFocusTo ?? (document.activeElement as HTMLElement | null);
    // Fokus inn på DIALOGEN, ikke på første fokuserbare (som er ✕ Lukk): det
    // første en skjermleser skal høre er hva dialogen er, ikke veien ut.
    rot.current?.focus();

    // Vertens innhold blir inert bak dialogen (DD-47). Søsknene til dialogens
    // portal-rot, ikke hele body — så widgeten selv består.
    const dialog = rot.current;
    const vertsnoder = Array.from(document.body.children).filter((n) => !n.contains(dialog));
    for (const n of vertsnoder) n.setAttribute('inert', '');

    return () => {
      for (const n of vertsnoder) n.removeAttribute('inert');
      // Fokus tilbake til vertens knapp, ikke til toppen av siden.
      const tilbake = sisteFokus.current;
      if (tilbake && document.contains(tilbake)) tilbake.focus();
    };
  }, [open, returnFocusTo]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !rot.current) return;
      // Fokusfellen: Tab fra siste går til første, Shift+Tab fra første til siste.
      const liste = fokuserbare(rot.current);
      if (liste.length === 0) return;
      const første = liste[0]!;
      const siste = liste[liste.length - 1]!;
      const aktiv = document.activeElement;
      if (e.shiftKey && (aktiv === første || !rot.current.contains(aktiv))) {
        e.preventDefault();
        siste.focus();
      } else if (!e.shiftKey && aktiv === siste) {
        e.preventDefault();
        første.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="innspel-rot innspel-scrim"
      data-form={form}
      data-innspel-theme={colorScheme}
      {...{ [WIDGET_ATTRIBUTE]: 'true' }}
      onMouseDown={(e) => {
        // Klikk på scrimmen lukker — men ikke et klikk som startet inne i
        // dialogen, og aldri når det ville kastet noe som er skrevet (C1).
        if (e.target === e.currentTarget && kanLukkeVedScrim) onClose();
      }}
    >
      <div ref={rot} role="dialog" aria-modal="true" aria-label={etikett} className="innspel-dialog" tabIndex={-1} data-testid="innspel-dialog">
        <p className="innspel-sr" role="status" aria-live="polite" data-testid="steg-annonsering">
          {etikett}
        </p>
        <div className="innspel-dialog-hode">
          {form === 'bunnark' ? <span className="innspel-handtak" aria-hidden="true" /> : <span className="innspel-dialog-tittel">Innspill</span>}
          <button type="button" className="innspel-ikonknapp" aria-label={tekst.lukkEsc} data-testid="lukk" onClick={onClose}>
            <Ikon navn="close-outline" />
          </button>
        </div>
        <div className="innspel-dialog-kropp">{children}</div>
        {footer ? <div className="innspel-dialog-fot">{footer}</div> : null}
      </div>
    </div>
  );
}

const FOKUSERBAR = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

function fokuserbare(rot: HTMLElement): HTMLElement[] {
  return Array.from(rot.querySelectorAll<HTMLElement>(FOKUSERBAR)).filter((el) => !el.hidden && el.getAttribute('aria-hidden') !== 'true');
}

/**
 * Bunnark under bruddpunktet fra tokens, modal over. Tallet leses fra
 * `--innspel-layout-breakpoint` så det bor ett sted; finnes ikke variabelen
 * (tokens.css ikke lastet), er formen modal.
 */
export function useForm(): 'modal' | 'bunnark' {
  const [form, setForm] = useState<'modal' | 'bunnark'>('modal');
  useEffect(() => {
    if (typeof matchMedia !== 'function') return;
    const bp = getComputedStyle(document.documentElement).getPropertyValue('--innspel-layout-breakpoint').trim();
    if (!bp) return;
    const mq = matchMedia(`(max-width: calc(${bp} - 1px))`);
    const oppdater = () => setForm(mq.matches ? 'bunnark' : 'modal');
    oppdater();
    mq.addEventListener('change', oppdater);
    return () => mq.removeEventListener('change', oppdater);
  }, []);
  return form;
}
